import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Copy, Check, ArrowRight, ExternalLink, Loader2 } from "lucide-react";
import { ASSETS } from "@/lib/constants";
import { CORE_LINKS, DOWNLOAD_LINKS, TOOLKIT_GROUPS, buildToolkitLinks } from "@/lib/links";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useNoIndex() {
  useEffect(() => {
    const el = document.createElement("meta");
    el.name = "robots";
    el.content = "noindex,nofollow";
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);
}

const LinkRow = ({ item }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(item.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      setCopied(false);
    }
  };
  return (
    <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`toolkit-link-${item.url}`}>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-navy">{item.label}</p>
        {item.use ? <p className="text-sm text-ink-500 mt-0.5 leading-relaxed">{item.use}</p> : null}
        <a
          href={item.url}
          target="_blank"
          rel="noreferrer"
          className="mt-1 inline-flex items-center gap-1 text-[12px] font-mono text-teal break-all hover:underline"
        >
          {item.url} <ExternalLink size={11} className="shrink-0" />
        </a>
      </div>
      <button
        type="button"
        onClick={copy}
        data-testid="toolkit-copy"
        className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border border-linen-border bg-white px-3.5 py-2 text-navy hover:border-teal transition-colors"
      >
        {copied ? <Check size={13} className="text-teal" /> : <Copy size={13} />}
        {copied ? "Copied" : "Copy link"}
      </button>
    </div>
  );
};

export default function ToolkitPage() {
  const { key } = useParams();
  const [toolkits, setToolkits] = useState(null);
  const [mlos, setMlos] = useState([]);
  useNoIndex();

  useEffect(() => {
    axios.get(`${API}/toolkits`).then((r) => setToolkits(r.data)).catch(() => setToolkits({}));
    axios.get(`${API}/mlos/public`).then((r) => setMlos(r.data || [])).catch(() => setMlos([]));
  }, []);

  const toolkit = toolkits?.[key];
  const origin = window.location.origin;

  const links = useMemo(
    () => buildToolkitLinks({ key, origin, apiBase: API, mlos }),
    [key, mlos, origin],
  );

  useEffect(() => {
    if (toolkit) document.title = `${toolkit.label} toolkit | Great Rate Check`;
  }, [toolkit]);

  if (toolkits === null) {
    return (
      <div className="min-h-screen bg-linen flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-teal" />
      </div>
    );
  }

  if (!toolkit) {
    return (
      <div className="min-h-screen bg-linen flex items-center justify-center px-6" data-testid="toolkit-not-found">
        <div className="text-center">
          <h1 className="font-heading text-2xl text-navy">That toolkit doesn't exist.</h1>
          <p className="mt-2 text-sm text-ink-500">Check the link, or head back to the main site.</p>
          <Link to="/" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-teal hover:text-navy">
            Great Rate Check home <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-linen text-navy" data-testid="toolkit-page">
      <header className="bg-navy text-white">
        <div className="container-grc py-8 sm:py-10">
          <div className="inline-flex items-center rounded-2xl bg-white/95 px-3 py-1.5">
            <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-8 w-auto" />
          </div>
          <p className="mt-6 text-[11px] font-bold tracking-[0.22em] uppercase text-lime">Partner reference · {links.length} link{links.length !== 1 ? "s" : ""}</p>
          <h1 className="mt-2 font-heading font-medium text-3xl sm:text-4xl" data-testid="toolkit-title">
            {toolkit.label} toolkit
          </h1>
          <p className="mt-3 max-w-2xl text-white/75 leading-relaxed" data-testid="toolkit-start-here">
            {toolkit.start_here}
          </p>
        </div>
      </header>

      <main className="container-grc py-8 sm:py-10 space-y-6">
        <div className="rounded-3xl bg-white border border-linen-border shadow-soft divide-y divide-linen-border overflow-hidden" data-testid="toolkit-links">
          {links.length === 0 ? (
            <p className="px-5 py-8 text-sm text-ink-500 text-center">No links in this toolkit yet.</p>
          ) : (
            links.map((l) => <LinkRow key={l.url} item={l} />)
          )}
        </div>

        {toolkit.steps?.length ? (
          <div className="rounded-3xl bg-white border border-linen-border shadow-soft p-6 sm:p-8" data-testid="toolkit-steps">
            <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-ink-500">How to use these</p>
            <ol className="mt-4 space-y-3">
              {toolkit.steps.map((step, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-ink-600 leading-relaxed">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-teal-light text-teal text-xs font-bold inline-flex items-center justify-center">
                    {i + 1}
                  </span>
                  {step}
                </li>
              ))}
            </ol>
          </div>
        ) : null}

        <div className="rounded-3xl bg-teal-light/60 border border-teal/20 px-6 py-5">
          <p className="text-sm text-navy leading-relaxed">
            <strong>You can hand this off.</strong> If you have an assistant or a marketing person on your
            team, send them this page — everything here is safe for them to use and send.
          </p>
          <p className="mt-2 text-[13px] text-ink-600 leading-relaxed">
            Bookmark this page rather than saving the email. If a link ever changes, this page updates itself.
          </p>
        </div>

        <p className="text-[11px] text-ink-400 text-center">
          Great Rate Check · NEXA Mortgage LLC · For internal / partner use.
        </p>
      </main>
    </div>
  );
}
